import { useQuery } from "@tanstack/react-query";
import useOPVPriceContract from "./useOPVPriceContract";
import { get } from "@/network/axiosClient";
import { StatusCodeResponse } from "@/network/constants";

/**
 * Fetch MBC-USDT price from API
 */
async function fetchOpvPrice(): Promise<number> {
  try {
    const result: any = await get("/coins/price/mbc-usdt");
    if (result?.status === StatusCodeResponse.SUCCESS) {
      return +result?.data?.data[0]?.p || 0;
    }
    return 0;
  } catch (error) {
    console.error("useGetOpvPrice error:", error);
    return 0;
  }
}

/**
 * Hook to get OPV/MBC price with automatic caching and refetching
 * Uses TanStack Query for:
 * - Request deduplication (multiple components calling this hook share the same request)
 * - Automatic caching (10 second stale time)
 * - Background refetching (every 10 seconds)
 * - Error handling and retry logic
 */
const useGetOpvPrice = (): any => {
  const contractMethod = useOPVPriceContract();

  const { data = 0 } = useQuery({
    queryKey: ["opv-price", "mbc-usdt"],
    queryFn: fetchOpvPrice,
    staleTime: 10000, // Data is fresh for 10 seconds
    refetchInterval: 10000, // Refetch every 10 seconds
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    retry: 3, // Retry failed requests 3 times
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });

  return [data, contractMethod];
};

export default useGetOpvPrice;
